import type { Breakpoint } from '@mui/material/styles';

import { useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import { useBoolean } from 'minimal-shared/hooks';
import { useLocation, useNavigationType } from 'react-router';

import { Box, Link, Alert, Button, Typography } from '@mui/material';

import { paths } from 'src/routes/paths';

import { Logo } from 'src/components/logo';

import { Footer } from './footer';
import { NavMobile } from './nav/mobile';
import { NavDesktop } from './nav/desktop';
import { UserPopover } from './user-popover';
import { MainSection } from '../core/main-section';
import { MenuButton } from '../components/menu-button';
import { LayoutSection } from '../core/layout-section';
import { HeaderSection } from '../core/header-section';
import { SignInButton } from '../components/sign-in-button';
import { NotificationsDrawer } from '../components/notifications-drawer';

import type { FooterProps } from './footer';
import type { NavMainProps } from './nav/types';
import type { MainSectionProps } from '../core/main-section';
import type { HeaderSectionProps } from '../core/header-section';
import type { LayoutSectionProps } from '../core/layout-section';

// ----------------------------------------------------------------------

type LayoutBaseProps = Pick<LayoutSectionProps, 'sx' | 'children' | 'cssVars'>;

export type MainLayoutProps = LayoutBaseProps & {
  layoutQuery?: Breakpoint;
  slotProps?: {
    header?: HeaderSectionProps;
    nav?: {
      data?: NavMainProps['data'];
    };
    main?: MainSectionProps;
    footer?: FooterProps;
  };
};

export function MainLayout({
  sx,
  cssVars,
  children,
  slotProps,
  layoutQuery = 'md',
}: MainLayoutProps) {
  const { value: open, onFalse: onClose, onTrue: onOpen } = useBoolean();
  const location = useLocation();
  const navigationType = useNavigationType();

  const { accessToken } = useSelector((state: any) => state.auth);
  const [hasAccessToken, setHasAccessToken] = useState(false);

  useEffect(() => {
    setHasAccessToken(accessToken);
  }, [accessToken]);

  // Global filtre temizleme navigation dinleyicisi
  useEffect(() => {
    const currentPath = location.pathname;

    // SessionStorage'dan önceki path'i oku
    let prevPath: string | null = null;
    try {
      prevPath = sessionStorage.getItem('hophop_prev_path');
    } catch (error) {
      console.error('SessionStorage okuma hatası:', error);
    }

    // İlk yükleme ise currentPath'i kaydet ve eğer doğrudan /aktivite'ye gelindiyse filtreleri sıfırla
    if (prevPath === null) {
      try {
        sessionStorage.setItem('hophop_prev_path', currentPath);
      } catch (error) {
        console.error('SessionStorage yazma hatası:', error);
      }
      if (/^\/aktivite$/.test(currentPath)) {
        try {
          sessionStorage.removeItem('hophop_filters');
        } catch (error) {
          console.error('İlk yüklemede /aktivite için filtre temizleme hatası:', error);
        }
      }
      return;
    }

    // Sayfa gerçekten değişti mi kontrol et
    if (currentPath === prevPath) {
      return;
    }

    // Önceki sayfa aktivite/blog/post alanında mıydı kontrol et
    const prevWasInContentArea =
      /^\/aktivite(\/[^/]+)?(\?.*)?$/.test(prevPath) || // /aktivite veya /aktivite/{id}
      /^\/blog\/[^/]+$/.test(prevPath) || // /blog/{id}
      /^\/post\/[^/]+$/.test(prevPath); // /post/{id}

    // Şu anki sayfa aktivite/blog/post alanında mı kontrol et
    const currentIsInContentArea =
      /^\/aktivite(\/[^/]+)?(\?.*)?$/.test(currentPath) || // /aktivite veya /aktivite/{id}
      /^\/blog\/[^/]+$/.test(currentPath) || // /blog/{id}
      /^\/post\/[^/]+$/.test(currentPath); // /post/{id}

    // Önceki sayfa pathname'ini güncelle
    try {
      sessionStorage.setItem('hophop_prev_path', currentPath);
    } catch (error) {
      console.error('SessionStorage yazma hatası:', error);
    }

    // Aktivite/blog/post alanından tamamen çıkış yapıldıysa filtreleri temizle
    if (prevWasInContentArea && !currentIsInContentArea) {
      // SessionStorage'ı temizle
      try {
        sessionStorage.removeItem('hophop_filters');
      } catch (error) {
        console.error('Global sessionStorage temizleme hatası:', error);
      }
    }

    // Aktiviteler liste sayfasına gelindiğinde, sadece detaydan geri (back) ile gelindiyse filtreleri KORU.
    // Bunun dışındaki tüm girişlerde (menüden tıklama, başka sayfadan push, direkt URL, forward vb.) filtreleri SIFIRLA.
    const comingFromActivityDetail = /^\/aktivite\/[^/]+$/.test(prevPath);
    const goingToActivityList = /^\/aktivite$/.test(currentPath);
    const isBackNavigation = navigationType === 'POP';

    if (goingToActivityList) {
      const shouldPreserve = comingFromActivityDetail && isBackNavigation;
      if (!shouldPreserve) {
        try {
          sessionStorage.removeItem('hophop_filters');
        } catch (error) {
          console.error('Aktivite listeye girişte filtre temizleme hatası:', error);
        }
      }
    }
  }, [location.pathname, navigationType]);

  const navData = [
    {
      title: 'Anasayfa',
      path: '/',
    },
    {
      title: 'Aktiviteler',
      path: paths.aktivite.root,
    },
    {
      title: 'Hakkımızda',
      path: paths.about,
    },
    {
      title: 'Nasıl Çalışır?',
      path: paths.howItWorks || '/nasil-calisir',
    },
    {
      title: 'Misyonumuz',
      path: paths.ourMission || '/misyonumuz',
    },
    {
      title: 'Organizatör Kaydı',
      path: paths.forOrganizer || '/organizator-kaydi',
    },
  ];

  const renderHeader = () => {
    const headerSlots: HeaderSectionProps['slots'] = {
      topArea: (
        <Alert
          icon={false}
          severity="error"
          sx={{ display: 'flex', justifyContent: 'center', borderRadius: 0 }}
        >
          <Link
            target="_blank"
            href="https://yonetim.hophop.com.tr"
            underline="none"
            color="inherit"
          >
            <Typography variant="body2" fontWeight="600" component="span">
              Atölye ve etkinliklerinizi listelemek ister misiniz? Organizatör kaydı için{' '}
              <Typography variant="body2" fontWeight="700" component="span" sx={{ ml: 0.5 }}>
                tıklayın.
              </Typography>
            </Typography>
          </Link>
        </Alert>
      ),
      leftArea: (
        <>
          {/** @slot Nav mobile */}
          <MenuButton
            onClick={onOpen}
            sx={(theme) => ({
              mr: 1,
              ml: -1,
              [theme.breakpoints.up(layoutQuery)]: { display: 'none' },
              color: 'common.white',
            })}
          />
          <NavMobile data={navData} open={open} onClose={onClose} />

          {/** @slot Logo */}
          <Logo
            sx={(theme) => ({
              display: 'none',
              filter: 'brightness(0) invert(1)',
              [theme.breakpoints.up(layoutQuery)]: { display: 'flex' },
            })}
          />
        </>
      ),
      centerArea: (
        <>
          {/** @slot Nav desktop */}
          <NavDesktop
            data={navData}
            sx={(theme) => ({
              display: 'none',
              [theme.breakpoints.up(layoutQuery)]: { mr: 2.5, display: 'flex' },
            })}
          />

          {/** @slot Logo */}
          <Logo
            sx={(theme) => ({
              display: 'flex',
              filter: 'brightness(0) invert(1)',
              [theme.breakpoints.up(layoutQuery)]: { display: 'none' },
              transform: 'translateX(20px)',
            })}
          />
        </>
      ),
      rightArea: (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
          {/** @slot Settings button */}
          {/* <SettingsButton /> */}

          {hasAccessToken ? (
            <>
              {/** @slot Notifications popover */}
              <NotificationsDrawer />

              {/** @slot UserPopover */}
              <UserPopover />
            </>
          ) : (
            <>
              {/** @slot Sign in button */}
              <SignInButton
                sx={(theme) => ({
                  display: 'none',
                  [theme.breakpoints.up(layoutQuery)]: {
                    display: 'inline-flex',
                    py: 1.25,
                    px: 1.5,
                  },
                })}
              />

              {/** @slot Purchase button */}
              <Button
                variant="outlined"
                rel="noopener"
                href={paths.auth.jwt.signUp}
                color="inherit"
                sx={(theme) => ({
                  display: 'none',
                  color: 'common.white',
                  borderColor: 'common.white',
                  [theme.breakpoints.up(layoutQuery)]: {
                    display: 'inline-flex',
                    py: 1.25,
                    px: 1.5,
                  },
                })}
              >
                Kayıt ol
              </Button>
            </>
          )}
        </Box>
      ),
    };

    return (
      <HeaderSection
        layoutQuery={layoutQuery}
        {...slotProps?.header}
        slots={{ ...headerSlots, ...slotProps?.header?.slots }}
        slotProps={slotProps?.header?.slotProps}
        sx={slotProps?.header?.sx}
      />
    );
  };

  const renderFooter = () => <Footer sx={slotProps?.footer?.sx} layoutQuery={layoutQuery} />;

  const renderMain = () => <MainSection {...slotProps?.main}>{children}</MainSection>;

  return (
    <LayoutSection
      /** **************************************
       * @Header
       *************************************** */
      headerSection={renderHeader()}
      /** **************************************
       * @Footer
       *************************************** */
      footerSection={renderFooter()}
      /** **************************************
       * @Styles
       *************************************** */
      cssVars={cssVars}
      sx={sx}
    >
      {renderMain()}
    </LayoutSection>
  );
}
